import { IconAlertTriangle, IconCheck, IconFile, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useTiposDocumento } from "@/features/catalogos/hooks/use-catalogos";
import type { CategoriaDef } from "@/features/documentos/constants";
import { useSubirDocumento } from "@/features/documentos/hooks/use-documentos-mutations";
import { Badge, Button, Card, Group, IconAction, Select, Stack, Text, ThemeIcon } from "@/components/ui";
import type { DocumentoVinculado } from "@/lib/types";
import { CategoriaDropzone, type CategoriaDropzoneEstado } from "./CategoriaDropzone";
import { DocumentoPendienteRow, DocumentoRow } from "./DocumentoRow";

interface Props {
  cat: CategoriaDef;
  documentos: DocumentoVinculado[];
  esperando: { id: string; nombre: string }[];
  solicitudId: string;
  numeroCfdi: number;
  puedeAdjuntar: boolean;
  /** El documento REAL recién subido · el padre lo trackea como "en despacho". */
  onSubidoPendiente: (doc: DocumentoVinculado) => void;
}

/** Archivo soltado en Adicional que espera su tipo de documento (P31). */
interface ArchivoPorClasificar {
  id: string;
  file: File;
  tipo: string | null;
  subiendo: boolean;
}

/**
 * Card por categoría · combina cabecera (label + badge de estado +
 * descripción), dropzone (con estado visual diferenciado: cargado /
 * vacío-requerido / vacío-opcional), y lista de documentos + pendientes.
 *
 * P31 · en ADICIONAL el tipo de documento se elige POR CADA archivo: el
 * dropzone siempre acepta (varios a la vez); cada archivo soltado queda
 * "por clasificar" con su propio Select y se sube al confirmar su tipo.
 */
export function CategoriaCard({ cat, documentos, esperando, solicitudId, numeroCfdi, puedeAdjuntar, onSubidoPendiente }: Props) {
  const subir = useSubirDocumento(solicitudId, cat.label, { onSubido: onSubidoPendiente });

  const esAdicional = cat.key === "Adicional";
  const { data: tiposDocumento } = useTiposDocumento();
  const [porClasificar, setPorClasificar] = useState<ArchivoPorClasificar[]>([]);

  const handleFiles = (files: FileList) => {
    if (esAdicional) {
      // Los adicionales NO se suben aún · cada uno espera su tipo (P31).
      setPorClasificar((prev) => [...prev, ...Array.from(files).map((file) => ({ id: crypto.randomUUID(), file, tipo: null as string | null, subiendo: false }))]);
      return;
    }
    // Single-file (ej. certificado de residencia): solo se sube el primer archivo
    // aunque se suelten varios (el input ya no permite multi-selección).
    const total = cat.single ? Math.min(files.length, 1) : files.length;
    for (let i = 0; i < total; i++) {
      subir.mutate({ numeroCfdi, categoria: cat.key, file: files[i] });
    }
  };

  const actualizar = (id: string, patch: Partial<ArchivoPorClasificar>) => setPorClasificar((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const subirClasificado = async (archivo: ArchivoPorClasificar) => {
    if (!archivo.tipo) return;
    actualizar(archivo.id, { subiendo: true });
    try {
      await subir.mutateAsync({ numeroCfdi, categoria: cat.key, file: archivo.file, tipoDocumento: archivo.tipo });
      setPorClasificar((prev) => prev.filter((a) => a.id !== archivo.id));
    } catch {
      // El hook ya notificó el error · el archivo se queda para reintentar.
      actualizar(archivo.id, { subiendo: false });
    }
  };

  const hayContenido = documentos.length > 0 || esperando.length > 0;
  const estadoDropzone: CategoriaDropzoneEstado = hayContenido ? "cargado" : cat.obligatorio ? "vacio-requerido" : "vacio-opcional";

  return (
    <Card
      padding="lg"
      style={{
        // Sutil acento en el borde de la card según estado · refuerza el visual interno.
        borderLeft: estadoDropzone === "cargado" ? "3px solid var(--color-multiva-400)" : estadoDropzone === "vacio-requerido" ? "3px solid var(--color-advertencia-300)" : undefined,
      }}
    >
      <Stack gap="md">
        <div>
          <Group gap={8} align="center">
            {hayContenido ? (
              <ThemeIcon size="sm" variant="filled" color="multiva">
                <IconCheck size={12} stroke={2.5} />
              </ThemeIcon>
            ) : cat.obligatorio ? (
              <ThemeIcon size="sm" variant="filled" color="multiva">
                <IconAlertTriangle size={12} stroke={2} />
              </ThemeIcon>
            ) : null}
            <Text fw={600} size="sm">
              {cat.label}
            </Text>
            {hayContenido ? (
              <Badge variant="light" color="multiva">
                Completo
              </Badge>
            ) : cat.obligatorio ? (
              <Badge variant="light" color="advertencia">
                Falta · obligatorio
              </Badge>
            ) : (
              <Badge variant="light" color="slate">
                Opcional
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" className="mt-1">
            {cat.descripcion}
          </Text>
        </div>

        {/* Single-file (ej. certificado de residencia): una vez cargado 1, se oculta el
            dropzone · para cambiarlo se elimina el actual y se sube otro. */}
        {puedeAdjuntar && !(cat.single && documentos.length >= 1) && (
          <CategoriaDropzone accept={cat.accept} cargando={subir.isPending} estado={estadoDropzone} onFiles={handleFiles} single={cat.single} />
        )}

        {/* P31 · archivos adicionales esperando su tipo · uno por fila. */}
        {porClasificar.length > 0 && (
          <Stack gap={6}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Clasifica cada documento para subirlo
            </Text>
            {porClasificar.map((a) => (
              <Group key={a.id} gap="sm" wrap="nowrap" className="rounded-lg border border-border p-2">
                <IconFile size={18} stroke={1.6} className="shrink-0" />
                <Text size="sm" truncate title={a.file.name} className="min-w-0 flex-1">
                  {a.file.name}
                </Text>
                <Select
                  placeholder="Tipo de documento…"
                  searchable
                  options={tiposDocumento.map((t) => ({ value: t.nombre, label: t.nombre }))}
                  value={a.tipo ?? undefined}
                  onValueChange={(v) => actualizar(a.id, { tipo: v })}
                  disabled={a.subiendo}
                  size="xs"
                  className="w-60"
                />
                <Button size="xs" leftSection={<IconUpload size={14} stroke={1.7} />} disabled={!a.tipo} loading={a.subiendo} onClick={() => void subirClasificado(a)}>
                  Subir
                </Button>
                <IconAction label={`Quitar ${a.file.name}`} variant="subtle" size="sm" disabled={a.subiendo} onClick={() => setPorClasificar((prev) => prev.filter((x) => x.id !== a.id))}>
                  <IconX size={16} stroke={1.7} />
                </IconAction>
              </Group>
            ))}
          </Stack>
        )}

        {hayContenido ? (
          <Stack gap={6}>
            {documentos.map((d) => (
              <DocumentoRow key={d.id} documento={d} solicitudId={solicitudId} puedeEliminar={puedeAdjuntar} />
            ))}
            {esperando.map((e) => (
              <DocumentoPendienteRow key={`pending-${e.id}`} nombre={e.nombre} />
            ))}
          </Stack>
        ) : !puedeAdjuntar ? (
          <Text size="xs" c="dimmed">
            Sin documentos en esta categoría.
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
